import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Code2, Shield, Sparkles, CheckCircle } from 'lucide-react';
import { motion } from 'framer-motion';

const UnifiedServicesShowcase = () => {
  const services = [
    {
      id: 'build',
      icon: Code2,
      headline: 'Build Your MVP or Scale Your App',
      description: 'From $998 MVPs to enterprise subscriptions—we build AI-powered web apps and internal tools that eliminate bottlenecks',
      features: [
        'Fixed Projects (Launch Lite, Core Build, Scale Partner)',
        'Subscription Services (Maintenance, Growth, Enterprise)',
        'Internal Tools & Dashboards',
        'Custom Web Applications'
      ],
      ctaText: 'Explore Build Services',
      ctaLink: '/services',
      gradient: 'from-primary/20 via-primary/10 to-transparent',
      iconBg: 'bg-primary/10',
      iconColor: 'text-primary'
    },
    {
      id: 'secure',
      icon: Shield,
      headline: 'AI Code Security & Vibe-Check Audits',
      description: 'Used AI tools to build fast? We audit, fix, and secure your "vibe-coded" MVP before it becomes a liability',
      features: [
        'AI-generated code vulnerability scanning',
        'Insecure defaults remediation',
        'Access control & authentication fixes',
        '3-month security guarantee'
      ],
      ctaText: 'Get Your Free Vibe Check',
      ctaLink: '/services/ai-code-security',
      gradient: 'from-blue-500/20 via-blue-500/10 to-transparent',
      iconBg: 'bg-blue-500/10',
      iconColor: 'text-blue-500'
    },
    {
      id: 'automate',
      icon: Sparkles,
      headline: 'WordPress AI Plugin Agents',
      description: 'Turn your WordPress site into an intelligent automation machine with custom AI-powered plugins',
      features: [
        'Activity tracking & monitoring',
        'AI-powered anomaly detection',
        'Security & compliance tools',
        'Custom plugin development'
      ],
      ctaText: 'Browse AI Plugins',
      ctaLink: '/wordpress-plugins',
      gradient: 'from-purple-500/20 via-purple-500/10 to-transparent',
      iconBg: 'bg-purple-500/10',
      iconColor: 'text-purple-500'
    }
  ];

  return (
    <section className="py-20 bg-gradient-to-br from-background via-muted/30 to-background relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute inset-0 bg-[linear-gradient(rgba(200,168,125,0.03)_1px,transparent_1px),linear-gradient(90deg,rgba(200,168,125,0.03)_1px,transparent_1px)] bg-[size:64px_64px]" />

      <div className="container mx-auto px-6 relative z-10">
        <div className="text-center mb-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6">
              Speed + Security + Automation = <span className="text-primary">Lovabuild</span>
            </h2>

            <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
              Your AI-first development partner for startups and growing businesses. We don't just build—we secure and automate.
            </p>
          </motion.div>
        </div>

        <div className="grid md:grid-cols-3 gap-8 max-w-7xl mx-auto">
          {services.map((service, index) => {
            const Icon = service.icon;
            return (
              <motion.div
                key={service.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <Card className="h-full bg-card/50 backdrop-blur border-border p-8 hover:border-primary/50 transition-all duration-300 group relative overflow-hidden">
                  {/* Gradient background */}
                  <div className={`absolute inset-0 bg-gradient-to-br ${service.gradient} opacity-0 group-hover:opacity-100 transition-opacity duration-300`} />

                  <div className="relative z-10">
                    <div className={`w-16 h-16 rounded-2xl ${service.iconBg} flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300`}>
                      <Icon className={`w-8 h-8 ${service.iconColor}`} />
                    </div>

                    <h3 className="text-2xl font-bold mb-4">{service.headline}</h3>

                    <p className="text-muted-foreground mb-6 leading-relaxed">
                      {service.description}
                    </p>

                    <ul className="space-y-3 mb-8">
                      {service.features.map((feature, idx) => (
                        <li key={idx} className="flex items-start gap-2">
                          <CheckCircle className={`w-5 h-5 ${service.iconColor} flex-shrink-0 mt-0.5`} />
                          <span className="text-sm text-muted-foreground">{feature}</span>
                        </li>
                      ))}
                    </ul>

                    <Link to={service.ctaLink} onClick={() => window.scrollTo(0, 0)}>
                      <Button
                        className="w-full group/btn"
                        variant={service.id === 'secure' ? 'default' : 'outline'}
                      >
                        {service.ctaText}
                        <Icon className="w-4 h-4 ml-2 group-hover/btn:translate-x-1 transition-transform" />
                      </Button>
                    </Link>
                  </div>
                </Card>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default UnifiedServicesShowcase;
